import { ArrowLeft, Edit02, Loading01 } from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import { useLarkWebhookStatus } from "@/api/lark-webhook-clients";
import { useMerchantRisksList } from "@/api/merchant-risks";
import { useSafetyPolicies } from "@/api/safety-policies";
import { useSequence, useUpdateSequence } from "@/api/sequences";
import { useTikTokAdsConnectionStatus } from "@/api/tiktok-ads";
import { PolicyRisksSidebar } from "@/components/safety-config/PolicyRisksSidebar";
import { POLICY_DESCRIPTIONS, POLICY_ICONS, POLICY_NAMES } from "@/components/safety-config/policy-copy";
import { POLICY_TOGGLEABLE_STEP_TYPES } from "@/components/safety-config/policy-toggleable-steps";
import { usePermissions } from "@/hooks/use-permissions";
import { SAFETY_ADMIN_REQUIRED } from "@/pages/safety-beta/permission-copy";
import { SequenceStepsPreview, type StepPatch } from "@/pages/sequences/sequence-steps-preview";
import { error as toastError, warning } from "@/providers/ToastProvider";

type PolicyDetailProps = {
  policyId: string;
  onBack: () => void;
};

const StatCard = ({ label, value, hint }: { label: string; value: string; hint: string }) => (
  <div className="flex-1 rounded-md border border-border-secondary bg-white p-5">
    <p className="text-sm font-medium text-tertiary">{label}</p>
    <p className="text-xl font-medium text-primary mt-2">{value}</p>
    <p className="text-sm text-tertiary">{hint}</p>
  </div>
);

export const PolicyDetail = ({ policyId, onBack }: PolicyDetailProps) => {
  const { data: policies } = useSafetyPolicies();
  const { data: merchantRisks } = useMerchantRisksList();
  const { canConfigureSafety } = usePermissions();
  const policy = policies?.find((p) => p.id === policyId);
  const { data: sequence } = useSequence(policy?.sequenceId ?? "", { enabled: !!policy?.sequenceId });
  const updateSequence = useUpdateSequence(policy?.sequenceId ?? "");
  const { data: adsStatus } = useTikTokAdsConnectionStatus();
  const { data: larkStatus } = useLarkWebhookStatus();

  // A `policy` id in the URL that matches nothing once policies have loaded (stale
  // link, deleted policy, wrong merchant) is cleared back to the bare tab URL instead
  // of leaving the "Policy not found" view stuck there.
  useEffect(() => {
    if (policies === undefined || policy) return;
    onBack();
    warning("We couldn't find that id, please try a different one");
  }, [policies, policy, onBack]);

  // Applied edits keyed by step number. Each commit (message saved / delay
  // chosen) persists immediately and also lives here so the preview reflects the
  // change before the refetch lands. Re-applying a patch the server already has
  // is idempotent, so there's no flicker once the query settles.
  const [patches, setPatches] = useState<Record<number, StepPatch>>({});

  // Policy-specific icon when defined, otherwise the generic edit glyph.
  const HeaderIcon = (policy && POLICY_ICONS[policy.type]) ?? Edit02;

  const steps = sequence?.steps ?? [];
  const editedSteps = useMemo(
    () => steps.map((s) => (patches[s.step] ? { ...s, ...patches[s.step] } : s)),
    [steps, patches],
  );

  const mappedRisks = useMemo(
    () =>
      (merchantRisks ?? [])
        .filter((mr) => mr.safetyPolicyId === policyId)
        .map((mr) => ({ id: mr.riskId, name: mr.risk.readableName, category: mr.risk.category })),
    [merchantRisks, policyId],
  );

  // Commit a single step edit and persist the whole step array straight away.
  const handleStepChange = (stepNumber: number, patch: StepPatch) => {
    const nextPatches = { ...patches, [stepNumber]: { ...patches[stepNumber], ...patch } };
    setPatches(nextPatches);

    const nextSteps = steps.map((s) => (nextPatches[s.step] ? { ...s, ...nextPatches[s.step] } : s));
    updateSequence.mutate(
      {
        steps: nextSteps.map((s) => ({
          step: s.step,
          type: s.type,
          delayDays: s.delayDays,
          sendAtHour: s.sendAtHour,
          sendAtTimezone: s.sendAtTimezone,
          messageTemplateId: s.messageTemplateId,
          customMessage: s.customMessage,
          imageId: s.imageId ?? null,
          branch: s.branch ?? null,
          branchCondition: s.branchCondition ?? null,
          enabled: s.enabled,
        })),
      },
      { onError: () => toastError("Couldn't save policy changes") },
    );
  };

  if (!policy) {
    return (
      <div className="p-8">
        <button type="button" className="flex items-center gap-1.5 text-sm text-tertiary mb-6" onClick={onBack}>
          <ArrowLeft className="w-4 h-4" /> Back to policies
        </button>
        <p className="text-sm text-tertiary">Policy not found.</p>
      </div>
    );
  }

  // Both figures are shares of the runs counted by the tile alongside this one: resolved
  // reached the policy's terminal action, in progress is still running. A run that failed or
  // was cancelled is in neither, so the two only add up to 100% while none have.
  const shareOfRuns = (runs: number) => `${Math.round((runs / policy.fired30d) * 100)}%`;
  const hasRuns = policy.fired30d > 0;
  const resolutionRate = hasRuns ? shareOfRuns(policy.resolved30d) : "—";
  const inProgressHint = hasRuns ? `${shareOfRuns(policy.inProgress30d)} in progress` : "No runs yet";

  return (
    <div className="flex flex-col p-8 min-h-screen bg-[#fefbf7]">
      <button
        type="button"
        className="flex items-center gap-1.5 text-sm text-tertiary hover:text-secondary transition-colors mb-5 w-max"
        onClick={onBack}
      >
        <ArrowLeft className="w-4 h-4" /> Back to policies
      </button>

      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-normal text-primary flex items-center gap-2">
            <HeaderIcon className="w-5 h-5 text-quaternary" strokeWidth={1.8} />
            {POLICY_NAMES[policy.type]}
          </h1>
          <p className="text-sm text-quaternary mt-1">{POLICY_DESCRIPTIONS[policy.type]}</p>
        </div>
        {updateSequence.isPending && (
          <span className="flex items-center gap-1.5 text-xs text-tertiary shrink-0 mt-1">
            <Loading01 className="w-3.5 h-3.5 animate-spin" />
            Saving…
          </span>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-6 items-start">
        <div className="flex-1 min-w-0">
          <SequenceStepsPreview
            type="safety"
            steps={editedSteps}
            triggerTitle="Risk accepted"
            triggerSubtitle="Risk is either confirmed by reviewer or auto-accepted"
            endLabel="Policy complete"
            editable={canConfigureSafety}
            onStepChange={canConfigureSafety ? handleStepChange : undefined}
            disabledReason={canConfigureSafety ? undefined : SAFETY_ADMIN_REQUIRED}
            adsBlocked={!adsStatus?.connected}
            larkBlocked={larkStatus?.configured === false}
            additionalToggleableStepTypes={POLICY_TOGGLEABLE_STEP_TYPES[policy.type]}
          />
        </div>
        <div className="w-full lg:w-80 shrink-0 flex flex-col gap-6">
          <PolicyRisksSidebar risks={mappedRisks} />
          <div className="flex gap-2">
            <StatCard label="Run last 30 days" value={String(policy.fired30d)} hint="creator videos" />
            <StatCard label="Resolution rate" value={resolutionRate} hint={inProgressHint} />
          </div>
        </div>
      </div>
    </div>
  );
};
